import { useState, useEffect } from 'react';
import { Badge, Card, SectionHeader } from '../../components/ui';

const DEFAULT_TRUST_SCORE = {
  score: 0,
  label: 'Needs Attention',
  breakdown: [
    { label: 'Financial Compliance', value: 0, weight: 30, color: '#4f46e5', tip: 'No data yet' },
    { label: 'Maintenance Collection', value: 0, weight: 25, color: '#10b981', tip: 'No data yet' },
    { label: 'Document Completeness', value: 0, weight: 20, color: '#06b6d4', tip: 'No data yet' },
    { label: 'Vendor Management', value: 0, weight: 15, color: '#f59e0b', tip: 'No data yet' },
    { label: 'Meeting Compliance', value: 0, weight: 10, color: '#8b5cf6', tip: 'No data yet' },
  ],
};

function computeTrustScore(data) {
  if (!data) {
    return DEFAULT_TRUST_SCORE;
  }

  if (typeof data.score === 'number' && Array.isArray(data.breakdown)) {
    return data;
  }

  const safe = {
    paidUnits: Number(data.paidUnits || 0),
    totalUnits: Math.max(1, Number(data.totalUnits || 0)),
    collected: Number(data.collected || 0),
    totalBilled: Math.max(1, Number(data.totalBilled || 0)),
    docCount: Number(data.docCount || 0),
    expectedDocs: Math.max(1, Number(data.expectedDocs || 8)),
    activeAmcs: Number(data.activeAmcs || 0),
    totalAmcs: Math.max(1, Number(data.totalAmcs || 1)),
    completedTasks: Number(data.completedTasks || 0),
    totalTasks: Math.max(1, Number(data.totalTasks || 1)),
  };

  const financial = Math.min(100, Math.round((safe.paidUnits / safe.totalUnits) * 100));
  const collection = Math.min(100, Math.round((safe.collected / safe.totalBilled) * 100));
  const documents = Math.min(100, Math.round((safe.docCount / safe.expectedDocs) * 100));
  const vendors = Math.min(100, Math.round((safe.activeAmcs / safe.totalAmcs) * 100));
  const governance = Math.min(100, Math.round((safe.completedTasks / safe.totalTasks) * 100));

  const score = Math.round(
    financial * 0.30 +
    collection * 0.25 +
    documents * 0.20 +
    vendors * 0.15 +
    governance * 0.10
  );

  return {
    score,
    label: score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : 'Needs Attention',
    breakdown: [
      { label: 'Financial Compliance', value: financial, weight: 30, color: '#4f46e5', tip: `${safe.paidUnits}/${safe.totalUnits} units paid` },
      { label: 'Maintenance Collection', value: collection, weight: 25, color: '#10b981', tip: `₹${(safe.collected / 100000).toFixed(1)}L of ₹${(safe.totalBilled / 100000).toFixed(1)}L` },
      { label: 'Document Completeness', value: documents, weight: 20, color: '#06b6d4', tip: `${safe.docCount} of ${safe.expectedDocs} key docs` },
      { label: 'Vendor Management', value: vendors, weight: 15, color: '#f59e0b', tip: `${safe.activeAmcs}/${safe.totalAmcs} AMCs healthy` },
      { label: 'Meeting Compliance', value: governance, weight: 10, color: '#8b5cf6', tip: `${safe.completedTasks}/${safe.totalTasks} tasks completed` },
    ],
  };
}

const getTranslationKey = (label) => {
  switch(label) {
    case 'Financial Compliance': return 'trust_financial';
    case 'Maintenance Collection': return 'trust_collection';
    case 'Document Completeness': return 'trust_docs';
    case 'Vendor Management': return 'trust_vendors';
    case 'Meeting Compliance': return 'trust_meetings';
    case 'Excellent': return 'trust_excellent';
    case 'Good': return 'trust_good';
    case 'Needs Attention': return 'trust_needs_attention';
    default: return label;
  }
};

export default function TrustScoreWidget({ data, t = (k) => k }) {
  const [mounted, setMounted] = useState(false);
  const [hovMetric, setHovMetric] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => setMounted(true), 300);
    return () => clearTimeout(timeout);
  }, []);

  const { score, breakdown, label } = computeTrustScore(data);
  const circ = 2 * Math.PI * 44;
  const offset = circ - (circ * score / 100);
  const scoreColor = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';

  return (
    <Card className="col-span-1 md:col-span-1 lg:col-span-2">
      <SectionHeader title={t('trust_title')} subtitle={t('trust_subtitle')} />
      <div className="flex gap-6">
        <div className="flex-shrink-0 flex flex-col items-center justify-center gap-2">
          <div className="relative">
            <svg width={112} height={112} viewBox="0 0 112 112">
              <circle cx={56} cy={56} r={44} fill="none" stroke="var(--color-border)" strokeOpacity="0.5" strokeWidth={10} />
              <circle
                cx={56}
                cy={56}
                r={44}
                fill="none"
                stroke={scoreColor}
                strokeWidth={10}
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={mounted ? offset : circ}
                style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'stroke-dashoffset 1.6s cubic-bezier(0.34,1.56,0.64,1)' }}
              />
              <text x={56} y={54} textAnchor="middle" dominantBaseline="central" fontSize={24} fontWeight="900" fill="var(--color-ink)">{score}</text>
              <text x={56} y={70} textAnchor="middle" dominantBaseline="central" fontSize={8} fill="#64748b" fontWeight="600" className="fill-slate-500 dark:fill-slate-400">/ 100</text>
            </svg>
          </div>
          <Badge color={score >= 80 ? 'green' : score >= 60 ? 'amber' : 'red'} dot>{t(getTranslationKey(label))}</Badge>
          <div className="text-[9px] text-muted text-center leading-relaxed">
            {t('trust_desc')}
          </div>
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {breakdown.map((metric, index) => (
            <div
              key={index}
              className="group cursor-default transition-colors"
              onMouseEnter={() => setHovMetric(index)}
              onMouseLeave={() => setHovMetric(null)}
              style={{ padding: '4px 6px', borderRadius: 8, background: hovMetric === index ? 'var(--color-border)' : 'transparent', opacity: hovMetric === index ? 1 : 0.8 }}
            >
              <div className="flex justify-between items-center text-xs mb-1 gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span style={{ width: 6, height: 6, borderRadius: 2, background: metric.color, display: 'inline-block', flexShrink: 0 }} />
                  <span className="text-muted dark:text-slate-400 truncate">{t(getTranslationKey(metric.label))}</span>
                  {hovMetric === index && (
                    <span style={{ fontSize: 9, fontWeight: 600, background: metric.color, padding: '2px 8px', borderRadius: 99 }} className="text-white shadow-sm whitespace-nowrap ml-1 flex-shrink-0">{metric.tip}</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {hovMetric === index && <span className="text-[9px] text-slate-500 dark:text-slate-400 whitespace-nowrap">Weight: {metric.weight}%</span>}
                  <span className="font-semibold text-ink dark:text-slate-100">{metric.value}%</span>
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{ width: mounted ? `${metric.value}%` : '0%', background: metric.color, transitionDelay: `${index * 0.12}s` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
